import type { RecordingRefFragment } from '@spotify/confidence-graphql';

type MetricDisplay = {
  label: string;
  format: (metric: number | undefined) => string;
  frustrationOrError?: boolean;
  userInteraction?: boolean;
};

export const getMetric = (
  metrics: RecordingRefFragment['metrics'] | undefined,
  key: string,
): number | undefined => {
  const value = metrics?.find(m => m.key === key)?.value?.value;
  return value ?? undefined;
};

export const formatMetric = (metric: number | undefined): string =>
  metric === undefined ? '-' : String(metric);

type SignalOption = { id: string; label: string };

export const METRIC_DISPLAY: Record<string, MetricDisplay> = {
  clicks: { label: 'Clicks', format: formatMetric, userInteraction: true },
  inputs: { label: 'Inputs', format: formatMetric, userInteraction: true },
  rageClicks: {
    label: 'Rage clicks',
    format: formatMetric,
    frustrationOrError: true,
    userInteraction: true,
  },
  deadClicks: {
    label: 'Dead clicks',
    format: formatMetric,
    frustrationOrError: true,
    userInteraction: true,
  },
  scrollBacks: {
    label: 'Scroll backs',
    format: formatMetric,
    userInteraction: true,
  },
  tabUnfocuses: {
    label: 'Tab unfocuses',
    format: formatMetric,
    userInteraction: true,
  },
  errorMessages: {
    label: 'Error messages',
    format: formatMetric,
    frustrationOrError: true,
  },
  networkRequestsFailed: {
    label: 'Failed requests',
    format: formatMetric,
    frustrationOrError: true,
  },
  networkRequests: { label: 'Network requests', format: formatMetric },
  consoleLogs: { label: 'Console logs', format: formatMetric },
  consoleWarnings: { label: 'Console warnings', format: formatMetric },
  consoleErrors: {
    label: 'Console errors',
    format: formatMetric,
    frustrationOrError: true,
  },
  routeChanges: {
    label: 'Route changes',
    format: formatMetric,
    userInteraction: true,
  },
};

export const SIGNAL_IDS: string[] = Object.keys(METRIC_DISPLAY);

export const getSignalLabel = (id: string): string =>
  METRIC_DISPLAY[id]?.label ?? id;

export const isFrustrationOrErrorSignal = (id: string): boolean =>
  METRIC_DISPLAY[id]?.frustrationOrError === true;

export const FRUSTRATION_OR_ERROR_SIGNAL_IDS: string[] = SIGNAL_IDS.filter(
  isFrustrationOrErrorSignal,
);

export const SIGNAL_OPTIONS: SignalOption[] = SIGNAL_IDS.map(id => ({
  id,
  label: getSignalLabel(id),
}));
